<template>
    <div>
        <LVector
            :zIndex="10"
            :defaultStyle="defaultStyle"
            :selectStyle="selectStyle"
            :selectAble="true"
            @selectFeature="handleSelectFeature"
        >
            <!-- 地块多边形 -->
            <LPolygonFeature
                v-for="field in fields"
                :key="'field-' + field.id"
                :id="String(field.id)"
                :positions="field.coordinates"
                :name="field.name"
                :styles="getFieldStyles(field)"
            />
            <!-- 取水点 -->
            <LPointFeature
                v-for="p in waterPoints"
                :key="'water-' + p.id"
                :id="String(p.id)"
                :longitude="p.lon"
                :latitude="p.lat"
                :name="p.name"
                :defaultStyle="waterStyle"
            />
            <!-- 起飞点 -->
            <LPointFeature
                v-for="p in takeoffPoints"
                :key="'takeoff-' + p.id"
                :id="String(p.id)"
                :longitude="p.lon"
                :latitude="p.lat"
                :name="p.name"
                :defaultStyle="takeoffStyle"
            />
        </LVector>
    </div>
</template>

<script>
import { Style, Fill, Stroke } from 'ol/style';
import { OMapMixin, LVector, LPointFeature, LPolygonFeature } from '@OpenLayersCommon';

const FIELD_FILL = 'rgba(73, 240, 253, 0.18)';
const FIELD_STROKE = '#49f0fd';
const WATER_COLOR = '#5bfffa';
const TAKEOFF_COLOR = '#ef8200';

// 作物名称到 imgs/crop 图标路径的映射
const CROP_ICON_MAP = {
    水稻: require('../imgs/crop/rice.png'),
    玉米: require('../imgs/crop/corn.png'),
    柑橘: require('../imgs/crop/citrus.png'),
    蔬菜: require('../imgs/crop/vegetable.png'),
    百香果: require('../imgs/crop/passion-fruit.png'),
};
const DEFAULT_CROP_ICON = require('../imgs/crop/vegetable.png');

/**
 * canvas 圆角矩形路径
 */
function roundRect (ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
}

/**
 * 气泡路径：圆角矩形 + 底部倒三角（连续一体，无内边线）
 * @param {*} tipBottom  倒三角顶点（尖端）坐标 [x, y]
 */
function bubblePath (ctx, x, y, w, h, r, tipX, tipY) {
    const half = 8; // 三角底边半宽
    ctx.beginPath();
    // 起点：左上圆角前
    ctx.moveTo(x + r, y);
    // 顶部边
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    // 右侧边
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    // 底边右半：直到三角底边右端点
    ctx.lineTo(tipX + half, y + h);
    // 倒三角右斜边
    ctx.lineTo(tipX, tipY);
    // 倒三角左斜边
    ctx.lineTo(tipX - half, y + h);
    // 底边左半：到左下圆角
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    // 左侧边
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
}

/**
 * 文本断行：按 maxWidth 切分，最多 maxLines 行
 *  - 优先按字符逐个累加，超过宽度则换行
 *  - 若最后一行的剩余内容超过宽度，截断并加 '...'
 */
function wrapText (ctx, text, maxWidth, maxLines) {
    if (!text) return [''];
    const lines = [];
    let current = '';
    for (let i = 0; i < text.length; i += 1) {
        const ch = text[i];
        const test = current + ch;
        if (ctx.measureText(test).width > maxWidth) {
            // 当前行已满
            if (lines.length === maxLines - 1) {
                // 最后一行：剩余内容用省略号截断
                let last = current;
                while (last.length > 0 && ctx.measureText(`${last}...`).width > maxWidth) {
                    last = last.slice(0, -1);
                }
                lines.push(`${last}...`);
                return lines;
            }
            lines.push(current);
            current = ch;
        } else {
            current = test;
        }
    }
    if (current) lines.push(current);
    return lines;
}

export default {
    name: 'MapLayer',
    mixins: [OMapMixin],
    components: { LVector, LPointFeature, LPolygonFeature },
    props: {
        fields: { type: Array, default: () => [] },
        waterPoints: { type: Array, default: () => [] },
        takeoffPoints: { type: Array, default: () => [] },
    },
    data() {
        return {
            currentZoom: 0,
            defaultStyle: {
                fill: { color: FIELD_FILL },
                stroke: { color: FIELD_STROKE, width: 2 },
            },
            selectStyle: {
                fill: { color: 'rgba(91, 240, 252, 0.4)' },
                stroke: { color: '#5bf0fc', width: 3 },
            },
        };
    },
    computed: {
        waterStyle() {
            return {
                image: {
                    type: 'circle',
                    radius: 12,
                    fill: { color: WATER_COLOR },
                    stroke: { color: '#fff', width: 2 },
                },
                text: { text: '取', font: 'bold 12px sans-serif', fill: { color: '#fff' } },
            };
        },
        takeoffStyle() {
            return {
                image: {
                    type: 'circle',
                    radius: 12,
                    fill: { color: TAKEOFF_COLOR },
                    stroke: { color: '#fff', width: 2 },
                },
                text: { text: '起', font: 'bold 12px sans-serif', fill: { color: '#fff' } },
            };
        },
    },
    async mounted() {
        try {
            const map = await this.getMap();
            this.olMap = map;
            const view = map && map.getView && map.getView();
            if (view) {
                this.currentZoom = view.getZoom() || 0;
                this.handleZoomChange = () => {
                    this.currentZoom = view.getZoom() || 0;
                };
                view.on('change:resolution', this.handleZoomChange);
            }
        } catch (e) {
            // 拿不到 map 时不阻断
            console.warn('[MapLayer] getMap failed', e);
        }
    },
    beforeDestroy() {
        if (this.olMap && this.handleZoomChange) {
            const view = this.olMap.getView && this.olMap.getView();
            view && view.un && view.un('change:resolution', this.handleZoomChange);
        }
    },
    methods: {
        /**
         * 地块样式：返回 OL Style 数组
         *  - [0] 多边形描边 + 填充
         *  - [1] 整体气泡（绿色圆形图标 + 地块名 + 面积），用 renderer canvas 自绘
         */
        getFieldStyles(field) {
            const self = this;

            const iconSrc = field.cropImgUrl || CROP_ICON_MAP[field.cropName] || DEFAULT_CROP_ICON;
            const cropImg = new Image();
            // 跨域图片必须显式设置 crossOrigin，否则 canvas 会被污染，OL hit detection 会抛 SecurityError
            if (/^https?:\/\//i.test(iconSrc)) {
                cropImg.crossOrigin = 'anonymous';
            }
            cropImg.src = iconSrc;
            const line1 = field.name || '';
            const line2 = `面积：${field.area ?? 0}亩`;

            const renderer = (coordinates, state) => {
                const ctx = state.context;
                // 缩放等级 <= 12 时，隐藏气泡弹窗
                if (self.currentZoom <= 12) return;
                // coordinates 对 Point 几何为 [x, y]
                const pt = Array.isArray(coordinates) ? coordinates : (coordinates && coordinates[0]);
                if (!pt || pt.length < 2) return;
                const x = pt[0];
                const y = pt[1];

                // 字体配置
                const titleFont = 'bold 14px sans-serif';
                const areaFont = '12px sans-serif';
                const lineHeight = 18;
                const maxBubbleWidth = 250; // 气泡最大宽度
                const paddingX = 12;
                const paddingY = 10;
                const iconSize = 36;
                const iconGap = 10;

                ctx.font = areaFont;
                const areaText = `面积：${field.area ?? 0}亩`;
                const w2 = ctx.measureText(areaText).width;

                // 名称可用宽度 = 最大气泡宽度 - 边距 - 图标 - 间距
                const maxTextWidth = maxBubbleWidth - paddingX * 2 - iconSize - iconGap;

                // 计算名称的可用宽度，对长文本做断行处理，最多 2 行
                ctx.font = titleFont;
                const nameText = line1 || '';
                const nameLines = wrapText(ctx, nameText, maxTextWidth, 2);

                // 气泡宽度 = 名称实际宽度 + 图标 + 间距 + 边距，与面积文字取大
                const titleWidth = nameLines.reduce((max, l) => Math.max(max, ctx.measureText(l).width), 0);
                const textColW = Math.max(titleWidth, w2);
                const boxW = Math.min(maxBubbleWidth, paddingX + iconSize + iconGap + textColW + paddingX);
                const boxH = paddingY * 2 + (nameLines.length * lineHeight) + lineHeight; // 名称N行 + 面积1行 + 上下padding
                const radius = 8;
                // 让底部倒三角的尖端指向地块中心 (x, y)
                // 尖端 y = top + boxH + tipH  =>  top = y - boxH - tipH
                const tipH = 10;
                const left = x - boxW / 2;
                const top = y - boxH - tipH;

                // 阴影
                ctx.shadowColor = 'rgba(0, 50, 100, 0.25)';
                ctx.shadowBlur = 8;
                ctx.shadowOffsetY = 2;

                // 气泡背景
                ctx.fillStyle = 'rgba(255, 255, 255, 0.96)';
                bubblePath(ctx, left, top, boxW, boxH, radius, x, top + boxH + 10);
                ctx.fill();
                ctx.shadowColor = 'transparent';
                ctx.shadowBlur = 0;
                ctx.shadowOffsetY = 0;

                // 边框
                ctx.strokeStyle = '#49f0fd';
                ctx.lineWidth = 1;
                bubblePath(ctx, left, top, boxW, boxH, radius, x, top + boxH + 10);
                ctx.stroke();

                // 作物图标（无背景圆，直接显示图标）
                const iconCx = left + paddingX + iconSize / 2;
                const iconCy = top + boxH / 2;
                // 图标外圈 2px 绿色圆形描边
                const draw = iconSize - 10;
                ctx.strokeStyle = '#5bcf4a';
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.arc(iconCx, iconCy, draw / 2, 0, Math.PI * 2);
                ctx.stroke();
                if (cropImg.complete && cropImg.naturalWidth) {
                    // 裁剪为圆形后绘制图标
                    ctx.save();
                    ctx.beginPath();
                    ctx.arc(iconCx, iconCy, draw / 2, 0, Math.PI * 2);
                    ctx.clip();
                    ctx.drawImage(
                        cropImg,
                        iconCx - draw / 2,
                        iconCy - draw / 2,
                        draw,
                        draw,
                    );
                    ctx.restore();
                }

                // 文字（名称多行 + 面积一行）
                const textX = left + paddingX + iconSize + iconGap;
                ctx.textBaseline = 'top';
                ctx.fillStyle = '#1f3a5f';
                ctx.font = titleFont;
                let textY = top + paddingY;
                nameLines.forEach((line) => {
                    ctx.fillText(line, textX, textY);
                    textY += lineHeight;
                });
                ctx.fillStyle = '#5b6b80';
                ctx.font = areaFont;
                ctx.fillText(areaText, textX, textY);
            };

            const polygonStyle = new Style({
                fill: new Fill({ color: FIELD_FILL }),
                stroke: new Stroke({ color: FIELD_STROKE, width: 2 }),
            });
            const centerPointStyle = new Style({
                geometry: (feat) => {
                    const g = feat.getGeometry();
                    if (!g || !g.getInteriorPoint) return null;
                    return g.getInteriorPoint();
                },
                renderer,
            });
            return [polygonStyle, centerPointStyle];
        },
        handleSelectFeature(feature) {
            if (!feature) return;
            const id = feature.getId();
            if (!id) return;
            const field = this.fields.find((f) => String(f.id) === String(id));
            if (field) {
                this.$emit('field-click', field);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
/* 图层组件本身不渲染 DOM */
</style>
